import arrayDiffer from 'array-differ';
import clsx from 'clsx';
import * as React from 'react';

import { type TableDefinitions, type TableView, table } from '@gitbook/doc-core';
import { PaletteButton } from '@gitbook/react-uikit';
import { type EditorContainer, useIsEditorFocused } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DragHandle,
    Heading,
    HeadlessTree,
    TreeItem,
    TreeItemContent,
    toast,
    treeDropIndicatorClassName,
    useDragAndDrop,
    useTreeData,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { capitalizeFirstLetter } from '../../../../utils';
import { createColumnDefsPalette } from '../ColumnDefinitions';
import type { TableRecordKV, TableRecordSubview } from '../types';
import { viewSpecs } from '../Views';
import { RecordColumnValueLine } from './RecordColumnValueLine';

/**
 * Modal to edit a record.
 */
export function RecordModal(props: {
    /** Key of the parent Table block */
    nodeKey: string;
    /** Editor instance */
    editor: EditorContainer;
    /** Is this value editable ? */
    readOnly: boolean;
    /** Record to display in the modal */
    record: TableRecordKV;
    /** All Records in the current view */
    records: TableRecordSubview;
    /** Current view in the table */
    view: TableView;
    /** Columns definition in the table */
    tableDefinition: TableDefinitions;
    /** Callback to open a record */
    openRecord: (recordId: string) => void;
    /** Callback when the user is closing the modal */
    onClose: () => void;
}): React.ReactElement {
    const {
        nodeKey,
        editor,
        readOnly,
        record,
        records,
        view,
        tableDefinition,
        openRecord,
        onClose,
    } = props;

    const [showHidden, setShowHidden] = React.useState(false);

    const viewSpec = viewSpecs[view.type];

    const visibleColumnIds = view.columns;

    const onReorder = useEventCallback(async (event) => {
        await toast.catch(async () => {
            if (event.keys.size !== 1) return;

            const movedKey = Array.from(event.keys)[0] as string;
            const movedItem = tree.getItem(movedKey);
            const targetItem = tree.getItem(event.target.key);
            const isSamePosition = movedKey === event.target.key;

            if (!targetItem || !movedItem || isSamePosition) return;

            const targetIndex = tree.getItemIndex(event.target.key);
            if (targetIndex === -1) return;

            tree.move(movedKey, null, targetIndex);
            editor.change((change) => {
                table.moveColumn({
                    change,
                    tableKey: nodeKey,
                    columnId: movedItem.value.id,
                    beforeColumnId: targetItem.value.id,
                });
            });

            toast.success('Item reordered');
        }, 'Error while reordering item');
    });

    const tree = useTreeData({ initialItems: visibleColumnIds.map((id) => ({ id })) });

    const { dragAndDropHooks } = useDragAndDrop({
        ...tree.getDragAndDropOptions(),
        getItems: (keys) => {
            return Array.from(keys).map((key: string) => {
                const item = tree.getItem(key);
                assert(item);
                return {
                    'text/plain': item.value.id,
                };
            });
        },
        onReorder,
    });

    const hiddenColumnIds = React.useMemo(() => {
        return arrayDiffer(Object.keys(tableDefinition), visibleColumnIds);
    }, [visibleColumnIds, view, tableDefinition]);

    const currentIndex = React.useMemo(() => {
        return records.findIndex((r) => r.key === record.key);
    }, [records, record.key]);
    const previousRecord = records[currentIndex - 1];
    const nextRecord = records[currentIndex + 1];

    const isEditorFocused = useIsEditorFocused();

    return (
        <DialogRoot defaultOpen onOpenChange={(open) => !open && onClose()}>
            <Dialog>
                <DialogHeader>
                    <Heading>{`Edit ${viewSpec.recordName} fields`}</Heading>
                </DialogHeader>
                <DialogBody>
                    <div
                        className="contents"
                        onKeyDownCapture={(event) => {
                            // Prevent navigation of the tree.
                            if (isEditorFocused) {
                                event.stopPropagation();
                            }
                        }}
                    >
                        <HeadlessTree
                            items={visibleColumnIds.map((id) => ({ id }))}
                            aria-label={`${viewSpec.recordName} fields`}
                            dragAndDropHooks={dragAndDropHooks}
                            className={clsx('group/tree', treeDropIndicatorClassName)}
                        >
                            {function renderItem(item) {
                                return (
                                    <TreeItem textValue={item.id} className="relative">
                                        <TreeItemContent>
                                            {readOnly ? null : <DragHandle />}
                                            <RecordColumnValueLine
                                                key={item.id}
                                                nodeKey={nodeKey}
                                                editor={editor}
                                                readOnly={readOnly}
                                                view={view}
                                                record={record}
                                                columnDefinition={tableDefinition[item.id]}
                                                tableDefinition={tableDefinition}
                                                isHidden={false}
                                            />
                                        </TreeItemContent>
                                    </TreeItem>
                                );
                            }}
                        </HeadlessTree>
                    </div>
                    {hiddenColumnIds.length > 0 || !readOnly ? (
                        <div className="flex justify-between">
                            {hiddenColumnIds.length > 0 ? (
                                <Button
                                    leadingIcon={showHidden ? Icon.ChevronUp : Icon.ChevronDown}
                                    kind="blank"
                                    size="medium"
                                    onPress={() => {
                                        setShowHidden((prev) => !prev);
                                    }}
                                    label={
                                        showHidden
                                            ? 'Hide'
                                            : `Show hidden ${viewSpec.definitionName}s`
                                    }
                                />
                            ) : null}
                            {readOnly ? null : (
                                <PaletteButton
                                    leadingIcon={Icon.Plus}
                                    kind="blank"
                                    size="medium"
                                    label={`Add ${viewSpec.definitionName}`}
                                    palette={() => {
                                        return createColumnDefsPalette((type) => {
                                            const newCol = table.createColumnFromType(type);
                                            editor.change((change) => {
                                                table.insertColumn(change, nodeKey, newCol);
                                            });
                                        });
                                    }}
                                />
                            )}
                        </div>
                    ) : null}
                    {showHidden ? (
                        <div className="mt-4 flex flex-col gap-4 border-t border-dashed pt-4">
                            {hiddenColumnIds.map((columnId) => {
                                return (
                                    <RecordColumnValueLine
                                        key={columnId}
                                        nodeKey={nodeKey}
                                        editor={editor}
                                        readOnly={readOnly}
                                        view={view}
                                        record={record}
                                        columnDefinition={tableDefinition[columnId]}
                                        tableDefinition={tableDefinition}
                                        isHidden
                                    />
                                );
                            })}
                        </div>
                    ) : null}
                </DialogBody>
                <DialogFooter>
                    <div className="flex w-full items-center">
                        {!readOnly && (
                            <Button
                                leadingIcon={Icon.Trashcan}
                                kind="danger"
                                size="medium"
                                label={`Delete ${viewSpec.recordName}`}
                                onPress={() => {
                                    toast.catch(() => {
                                        editor.change((change) => {
                                            table.removeRecord(change, nodeKey, record.key);
                                        });

                                        toast.success(
                                            `${capitalizeFirstLetter(viewSpec.recordName)} deleted`
                                        );
                                    }, 'Error while removing record');
                                }}
                            />
                        )}
                        <div className="ml-auto flex items-center gap-4">
                            <Button
                                leadingIcon={Icon.ChevronLeft}
                                kind="secondary"
                                size="medium"
                                iconOnly
                                label={`Previous ${viewSpec.recordName}`}
                                disabled={!previousRecord}
                                onPress={() => previousRecord && openRecord(previousRecord.key)}
                            />
                            <span className="text-muted">{`${capitalizeFirstLetter(viewSpec.recordName)} ${currentIndex + 1} of ${records.length}`}</span>
                            <Button
                                leadingIcon={Icon.ChevronRight}
                                kind="secondary"
                                size="medium"
                                iconOnly
                                label={`Next ${viewSpec.recordName}`}
                                disabled={!nextRecord}
                                onPress={() => nextRecord && openRecord(nextRecord.key)}
                            />
                        </div>
                    </div>
                </DialogFooter>
            </Dialog>
        </DialogRoot>
    );
}
